import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaTrash,
  FaLightbulb,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { generateResumePDF } from "../api/api"; // Import the new API function
import { supabase } from "../supabaseClient"; // Make sure this path is correct
import jwtDecode from "jwt-decode"; // Make sure you have this package installed
import ResponsibilityToggle from "../components/ResponsibilityToggle";

function ResumeEditor() {
  const location = useLocation();
  const { resume } = location.state;

  // Main resume data state
  const [editedResume, setEditedResume] = useState({
    personal_info: resume.personal_info || { name: "", phone: "", email: "" },
    experiences: {
      work_experiences: resume.experiences.work_experiences || [],
      projects: resume.experiences.projects || [],
      education: resume.experiences.education || [],
    },
    additional_information: {
      "Technical Skills": {
        current:
          resume.additional_information?.["Technical Skills"]?.current || [],
        suggested:
          resume.additional_information?.["Technical Skills"]?.suggested || [],
      },
      Languages: resume.additional_information?.Languages || [],
    },
  });

  // UI state to track display mode for each responsibility
  const [displayModes, setDisplayModes] = useState(() => {
    const modes = {};
    // Initialize work experiences
    editedResume.experiences.work_experiences.forEach((exp, expIndex) => {
      exp.responsibilities.forEach((_, respIndex) => {
        modes[`work_experiences-${expIndex}-${respIndex}`] = "suggested";
      });
    });
    // Initialize projects
    editedResume.experiences.projects.forEach((project, projectIndex) => {
      project.responsibilities.forEach((_, respIndex) => {
        modes[`projects-${projectIndex}-${respIndex}`] = "suggested";
      });
    });
    // Initialize technical skills
    const technicalSkills =
      editedResume.additional_information["Technical Skills"];
    const skillsLength = Math.max(
      technicalSkills.current.length,
      technicalSkills.suggested.length
    );
    for (let i = 0; i < skillsLength; i++) {
      modes[`technical_skills-${i}`] = "suggested";
    }
    return modes;
  });

  const [jwtToken, setJwtToken] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    personal_info: true,
    work_experiences: true,
    projects: resume.experiences.projects ? true : false,
    education: true,
    technical_skills: true,
    languages: true,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        setJwtToken(session.access_token);
      }
    };

    getSession();
  }, []);

  const handleInputChange = (e, expIndex, type, respIndex, isCurrent) => {
    const { value } = e.target;
    setEditedResume((prev) => {
      const newResume = { ...prev };
      const responsibilities =
        newResume.experiences[type][expIndex].responsibilities;
      const currentMode = displayModes[`${expIndex}-${respIndex}`];

      // Update the text based on the current display mode
      if (currentMode === "current") {
        responsibilities[respIndex].current = value;
      } else {
        responsibilities[respIndex].suggested = value;
      }

      return newResume;
    });
  };

  const handleDeleteResponsibility = (experienceIndex, respIndex, type) => {
    // Log the indices to verify which responsibility we're trying to delete
    console.log(
      `Deleting: experience ${experienceIndex}, responsibility ${respIndex}`
    );

    setEditedResume((prevResume) => {
      const newResume = { ...prevResume };
      const responsibilities =
        newResume.experiences[type][experienceIndex].responsibilities;

      // Verify we have the correct responsibility before deleting
      if (responsibilities[respIndex]) {
        responsibilities.splice(respIndex, 1);
      }

      return newResume;
    });

    // Update display modes
    setDisplayModes((prev) => {
      const newModes = { ...prev };

      // Remove the mode for the deleted responsibility
      delete newModes[`${type}-${experienceIndex}-${respIndex}`];

      // Shift the modes for all subsequent responsibilities in this experience
      Object.keys(newModes)
        .filter((key) => {
          const [typeKey, expIdx, respIdx] = key.split("-");
          return (
            typeKey === type &&
            Number(expIdx) === experienceIndex &&
            Number(respIdx) > respIndex
          );
        })
        .forEach((key) => {
          const [typeKey, expIdx, respIdx] = key.split("-");
          newModes[`${typeKey}-${expIdx}-${Number(respIdx) - 1}`] =
            newModes[key];
          delete newModes[key];
        });

      return newModes;
    });
  };

  const toggleSection = (section) => {
    console.log(resume);
    console.log(editedResume);
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSaveChanges = async () => {
    try {
      if (!jwtToken) {
        console.error("No JWT token available");
        return;
      }

      const resumeToGenerate = {
        ...editedResume,
        experiences: {
          ...editedResume.experiences,
          work_experiences: editedResume.experiences.work_experiences.map(
            (exp, expIndex) => ({
              ...exp,
              responsibilities: exp.responsibilities.map((resp, respIndex) => {
                const mode =
                  displayModes[`work_experiences-${expIndex}-${respIndex}`];
                return {
                  current: mode === "current" ? resp.current : "",
                  suggested: mode === "current" ? "" : resp.suggested,
                };
              }),
            })
          ),
          projects: editedResume.experiences.projects.map(
            (project, projectIndex) => ({
              ...project,
              responsibilities: project.responsibilities.map(
                (resp, respIndex) => {
                  const mode =
                    displayModes[`projects-${projectIndex}-${respIndex}`];
                  return {
                    current: mode === "current" ? resp.current : "",
                    suggested: mode === "current" ? "" : resp.suggested,
                  };
                }
              ),
            })
          ),
        },
        additional_information: {
          ...editedResume.additional_information,
          "Technical Skills": {
            current:
              displayModes["technical_skills"] === "current"
                ? editedResume.additional_information["Technical Skills"]
                    .current
                : [],
            suggested:
              displayModes["technical_skills"] === "suggested"
                ? editedResume.additional_information["Technical Skills"]
                    .suggested
                : [],
          },
          Languages: editedResume.additional_information.Languages,
        },
      };

      const response = await generateResumePDF(resumeToGenerate, jwtToken);

      navigate("/resumes", { state: { resume: resumeToGenerate } });
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setEditedResume((prevResume) => ({
      ...prevResume,
      personal_info: {
        ...prevResume.personal_info,
        [field]: value,
      },
    }));
  };

  const handleModeChange = (index, respIndex, type, newMode) => {
    setDisplayModes((prev) => ({
      ...prev,
      [`${type}-${index}-${respIndex}`]: newMode,
    }));
  };

  const [responsibilities, setResponsibilities] = useState([
    { id: 1, mode: "suggested", suggestedText: "", currentText: "" },
    { id: 2, mode: "suggested", suggestedText: "", currentText: "" },
    // Add more responsibilities as needed
  ]);

  const handleToggleChange = (id) => {
    setResponsibilities((prev) =>
      prev.map((resp) =>
        resp.id === id
          ? {
              ...resp,
              mode: resp.mode === "suggested" ? "current" : "suggested",
            }
          : resp
      )
    );
  };

  const handleTextChange = (id, text, isCurrent) => {
    setResponsibilities((prev) =>
      prev.map((resp) =>
        resp.id === id
          ? { ...resp, [isCurrent ? "currentText" : "suggestedText"]: text }
          : resp
      )
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 mb-16">
      {/* Personal Information Section */}
      <div className="mb-8 bg-white shadow-md rounded-lg overflow-visible">
        <div
          className="bg-purple-50 p-4 flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection("personal_info")}
        >
          <h3 className="text-xl font-semibold text-gray-800 text-left">
            Personal Information
          </h3>
          {expandedSections.personal_info ? (
            <FaChevronUp className="text-gray-600" />
          ) : (
            <FaChevronDown className="text-gray-600" />
          )}
        </div>
        {expandedSections.personal_info && (
          <div className="p-4 overflow-visible">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Name
              </label>
              <input
                type="text"
                value={editedResume.personal_info.name}
                onChange={(e) =>
                  handlePersonalInfoChange("name", e.target.value)
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-left"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Phone
              </label>
              <input
                type="tel"
                value={editedResume.personal_info.phone}
                onChange={(e) =>
                  handlePersonalInfoChange("phone", e.target.value)
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-left"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Email
              </label>
              <input
                type="email"
                value={editedResume.personal_info.email}
                onChange={(e) =>
                  handlePersonalInfoChange("email", e.target.value)
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-left"
              />
            </div>
          </div>
        )}
      </div>

      {/* Education Section */}
      {editedResume.experiences.education && (
        <div className="mb-8 bg-white shadow-md rounded-lg overflow-visible">
          <div
            className="bg-purple-50 p-4 flex justify-between items-center cursor-pointer"
            onClick={() => toggleSection("education")}
          >
            <h3 className="text-xl font-semibold text-gray-800 text-left">
              Education
            </h3>
            {expandedSections.education ? (
              <FaChevronUp className="text-gray-600" />
            ) : (
              <FaChevronDown className="text-gray-600" />
            )}
          </div>
          {expandedSections.education && (
            <div className="p-4 overflow-visible">
              {editedResume.experiences.education.map((edu, index) => (
                <div
                  key={index}
                  className="mb-6 p-4 bg-gray-50 rounded-lg relative"
                >
                  <button
                    onClick={() => {
                      const newEducation =
                        editedResume.experiences.education.filter(
                          (_, i) => i !== index
                        );
                      setEditedResume((prev) => ({
                        ...prev,
                        experiences: {
                          ...prev.experiences,
                          education: newEducation,
                        },
                      }));
                    }}
                    className="text-red-400 hover:text-red-600 absolute top-5 right-4"
                  >
                    <FaTrash />
                  </button>
                  <div className="mb-4">
                    <label className="block mt-6 text-sm font-medium text-gray-700 text-left">
                      Degree
                    </label>
                    <input
                      type="text"
                      value={edu.degree}
                      onChange={(e) => {
                        const newEducation = [
                          ...editedResume.experiences.education,
                        ];
                        newEducation[index].degree = e.target.value;
                        setEditedResume((prev) => ({
                          ...prev,
                          experiences: {
                            ...prev.experiences,
                            education: newEducation,
                          },
                        }));
                      }}
                      className="flex border border-gray-300 p-2 rounded w-full text-left"
                      placeholder="Degree"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 text-left">
                      Institution
                    </label>
                    <input
                      type="text"
                      value={edu.institution}
                      onChange={(e) => {
                        const newEducation = [
                          ...editedResume.experiences.education,
                        ];
                        newEducation[index].institution = e.target.value;
                        setEditedResume((prev) => ({
                          ...prev,
                          experiences: {
                            ...prev.experiences,
                            education: newEducation,
                          },
                        }));
                      }}
                      className="flex border border-gray-300 p-2 rounded w-full text-left"
                      placeholder="Institution"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 text-left">
                      Dates
                    </label>
                    <input
                      type="text"
                      value={edu.dates}
                      onChange={(e) => {
                        const newEducation = [
                          ...editedResume.experiences.education,
                        ];
                        newEducation[index].dates = e.target.value;
                        setEditedResume((prev) => ({
                          ...prev,
                          experiences: {
                            ...prev.experiences,
                            education: newEducation,
                          },
                        }));
                      }}
                      className="flex border border-gray-300 p-2 w-full rounded text-left"
                      placeholder="Dates"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 text-left">
                      Details
                    </label>
                    <textarea
                      value={edu.details.join(", ")}
                      onChange={(e) => {
                        const newEducation = [
                          ...editedResume.experiences.education,
                        ];
                        newEducation[index].details =
                          e.target.value.split(", ");
                        setEditedResume((prev) => ({
                          ...prev,
                          experiences: {
                            ...prev.experiences,
                            education: newEducation,
                          },
                        }));
                      }}
                      className="flex border border-gray-300 p-2 rounded w-full text-left"
                      placeholder="Details (comma separated)"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Work Experiences Section */}
      <div className="mb-8 bg-white shadow-md rounded-lg overflow-visible">
        <div
          className="bg-purple-50 p-4 flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection("work_experiences")}
        >
          <h3 className="text-xl font-semibold text-gray-800 text-left">
            Work Experiences
          </h3>
          {expandedSections.work_experiences ? (
            <FaChevronUp className="text-gray-600" />
          ) : (
            <FaChevronDown className="text-gray-600" />
          )}
        </div>
        {expandedSections.work_experiences && (
          <div className="p-4 overflow-visible">
            {editedResume.experiences.work_experiences.map(
              (experience, index) => (
                <div key={index} className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-left">
                    {experience.title}
                  </h4>
                  <p className="text-left">{experience.company}</p>
                  <p className="text-left">{experience.dates}</p>
                  {experience.responsibilities.map((resp, respIndex) => (
                    <div key={respIndex} className="mb-4">
                      <div className="flex items-center gap-4 mb-2">
                        <ResponsibilityToggle
                          mode={
                            displayModes[
                              `work_experiences-${index}-${respIndex}`
                            ]
                          }
                          onChange={(newMode) =>
                            handleModeChange(
                              index,
                              respIndex,
                              "work_experiences",
                              newMode
                            )
                          }
                        />
                        <textarea
                          value={
                            resp[
                              displayModes[
                                `work_experiences-${index}-${respIndex}`
                              ]
                            ]
                          }
                          onChange={(e) =>
                            handleInputChange(
                              e,
                              index,
                              "work_experiences",
                              respIndex
                            )
                          }
                          rows={2}
                          className="flex-1 border border-gray-300 p-2 rounded"
                          placeholder={`${
                            displayModes[
                              `work_experiences-${index}-${respIndex}`
                            ]
                          } Responsibility`}
                        />
                        <button
                          onClick={() =>
                            handleDeleteResponsibility(
                              index,
                              respIndex,
                              "work_experiences"
                            )
                          }
                          className="text-red-400 hover:text-red-600"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Projects Section */}
      {editedResume.experiences.projects && (
        <div className="mb-8 bg-white shadow-md rounded-lg overflow-visible">
          <div
            className="bg-purple-50 p-4 flex justify-between items-center cursor-pointer"
            onClick={() => toggleSection("projects")}
          >
            <h3 className="text-xl font-semibold text-gray-800 text-left">
              Projects
            </h3>
            {expandedSections.projects ? (
              <FaChevronUp className="text-gray-600" />
            ) : (
              <FaChevronDown className="text-gray-600" />
            )}
          </div>
          {expandedSections.projects && (
            <div className="p-4 overflow-visible">
              {editedResume.experiences.projects.map((project, index) => (
                <div key={index} className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-left">
                    {project.title}
                  </h4>
                  {project.responsibilities.map((resp, respIndex) => (
                    <div key={respIndex} className="mb-4">
                      <div className="flex items-center gap-4 mb-2">
                        <ResponsibilityToggle
                          mode={displayModes[`projects-${index}-${respIndex}`]}
                          onChange={(newMode) =>
                            handleModeChange(
                              index,
                              respIndex,
                              "projects",
                              newMode
                            )
                          }
                        />
                        <textarea
                          value={
                            resp[displayModes[`projects-${index}-${respIndex}`]]
                          }
                          onChange={(e) =>
                            handleInputChange(e, index, "projects", respIndex)
                          }
                          rows={2}
                          className="flex-1 border border-gray-300 p-2 rounded"
                          placeholder={`${
                            displayModes[`projects-${index}-${respIndex}`]
                          } Responsibility`}
                        />
                        <button
                          onClick={() =>
                            handleDeleteResponsibility(
                              index,
                              respIndex,
                              "projects"
                            )
                          }
                          className="text-red-400 hover:text-red-600"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Technical Skills Section */}
      <div className="mb-8 bg-white shadow-md rounded-lg overflow-visible">
        <div
          className="bg-purple-50 p-4 flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection("technical_skills")}
        >
          <h3 className="text-xl font-semibold text-gray-800 text-left">
            Technical Skills
          </h3>
          {expandedSections.technical_skills ? (
            <FaChevronUp className="text-gray-600" />
          ) : (
            <FaChevronDown className="text-gray-600" />
          )}
        </div>
        {expandedSections.technical_skills && (
          <div className="p-4 overflow-visible">
            <div className="flex items-center gap-4 mb-2">
              <ResponsibilityToggle
                mode={displayModes["technical_skills"] || "suggested"}
                onChange={(newMode) =>
                  setDisplayModes((prev) => ({
                    ...prev,
                    technical_skills: newMode,
                  }))
                }
              />
              <textarea
                value={editedResume.additional_information["Technical Skills"][
                  displayModes["technical_skills"] || "suggested"
                ].join(", ")}
                onChange={(e) => {
                  const newSkills = {
                    ...editedResume.additional_information["Technical Skills"],
                  };
                  newSkills[displayModes["technical_skills"]] = e.target.value
                    .split(", ")
                    .filter((skill) => skill.trim());
                  setEditedResume((prev) => ({
                    ...prev,
                    additional_information: {
                      ...prev.additional_information,
                      "Technical Skills": newSkills,
                    },
                  }));
                }}
                rows={2}
                className="flex-1 border border-gray-300 p-2 rounded"
                placeholder={`${
                  displayModes["technical_skills"] || "suggested"
                } Technical Skills (comma separated)`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Languages Section */}
      <div className="mb-8 bg-white shadow-md rounded-lg overflow-visible">
        <div
          className="bg-purple-50 p-4 flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection("languages")}
        >
          <h3 className="text-xl font-semibold text-gray-800 text-left">
            Languages
          </h3>
          {expandedSections.languages ? (
            <FaChevronUp className="text-gray-600" />
          ) : (
            <FaChevronDown className="text-gray-600" />
          )}
        </div>
        {expandedSections.languages && (
          <div className="p-4 overflow-visible">
            {editedResume.additional_information.Languages.map(
              (language, index) => (
                <div key={index} className="flex items-center mb-2">
                  <input
                    type="text"
                    value={language}
                    onChange={(e) => {
                      const newLanguages = [
                        ...editedResume.additional_information.Languages,
                      ];
                      newLanguages[index] = e.target.value;
                      setEditedResume((prev) => ({
                        ...prev,
                        additional_information: {
                          ...prev.additional_information,
                          Languages: newLanguages,
                        },
                      }));
                    }}
                    className="border border-gray-300 p-2 rounded w-full"
                    placeholder="Language"
                  />
                  <button
                    onClick={() => {
                      const newLanguages =
                        editedResume.additional_information.Languages.filter(
                          (_, i) => i !== index
                        );
                      setEditedResume((prev) => ({
                        ...prev,
                        additional_information: {
                          ...prev.additional_information,
                          Languages: newLanguages,
                        },
                      }));
                    }}
                    className="text-red-400 hover:text-red-600 ml-2"
                  >
                    <FaTrash />
                  </button>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Generate Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveChanges}
          className="bg-purple-400 hover:bg-purple-600 text-white px-4 py-2 rounded"
        >
          Generate Resume
        </button>
      </div>
    </div>
  );
}

export default ResumeEditor;
